export interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  regex: RegExp;
  description: string;
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  {
    code: "zh",
    name: "中文 / 汉字",
    nativeName: "中文",
    flag: "🇨🇳",
    regex: /[\u4e00-\u9fa5\u3400-\u4dbf\uf900-\ufaff]/,
    description: "过滤昵称中包含任何汉字/中文字符的成员"
  },
  {
    code: "ar",
    name: "阿拉伯语 / 波斯语",
    nativeName: "العربية / فارسی",
    flag: "🇸🇦",
    regex: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/,
    description: "过滤包含阿拉伯字母、波斯字母或乌尔都语字符的成员"
  },
  {
    code: "ru",
    name: "俄语 / 西里尔文",
    nativeName: "Русский",
    flag: "🇷🇺",
    regex: /[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/,
    description: "过滤包含俄语、乌克兰语、白俄罗斯语等西里尔字母的成员"
  },
  {
    code: "en",
    name: "英语 / 拉丁字母",
    nativeName: "English / Latin",
    flag: "🇬🇧",
    regex: /[a-zA-Z]/,
    description: "过滤包含标准拉丁字母(A-Z, a-z)的成员"
  },
  {
    code: "th",
    name: "泰语",
    nativeName: "ภาษาไทย",
    flag: "🇹🇭",
    regex: /[\u0E00-\u0E7F]/,
    description: "过滤包含泰文字符的成员"
  },
  {
    code: "hi",
    name: "印地语 / 天城文",
    nativeName: "हिन्दी",
    flag: "🇮🇳",
    regex: /[\u0900-\u097F]/,
    description: "过滤包含印地语、梵文等天城文字符的成员"
  },
  {
    code: "ja",
    name: "日语 (假名)",
    nativeName: "日本語 (かな)",
    flag: "🇯🇵",
    regex: /[\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF]/,
    description: "过滤包含日文平假名或片假名的成员"
  },
  {
    code: "ko",
    name: "韩语 / 谚文",
    nativeName: "한국어",
    flag: "🇰🇷",
    regex: /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/,
    description: "过滤包含韩文谚文字符的成员"
  },
  {
    code: "my",
    name: "缅甸语",
    nativeName: "မြန်မာစာ",
    flag: "🇲🇲",
    regex: /[\u1000-\u109F\uA9E0-\uA9FF]/,
    description: "过滤包含缅甸文字符的成员"
  },
  {
    code: "he",
    name: "希伯来语",
    nativeName: "עברית",
    flag: "🇮🇱",
    regex: /[\u0590-\u05FF]/,
    description: "过滤包含希伯来字母的成员"
  },
  {
    code: "vi",
    name: "越南语 (声调)",
    nativeName: "Tiếng Việt",
    flag: "🇻🇳",
    regex: /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđÀÁẠẢÃÂẦẤẬẨẪĂẰẮẶẲẴÈÉẸẺẼÊỀẾỆỂỄÌÍỊỈĨÒÓỌỎÕÔỒỐỘỔỖƠỜỚỢỞỠÙÚỤỦŨƯỪỨỰỬỮỲÝỴỶỸĐ]/,
    description: "过滤包含越南语特殊音调字符的成员"
  }
];

/**
 * Detect which configured languages are present in the provided text (returns language codes).
 */
export function detectLanguagesInText(text: string): string[] {
  if (!text || typeof text !== "string") return [];
  const detected: string[] = [];
  for (const lang of LANGUAGE_OPTIONS) {
    if (lang.regex.test(text)) {
      detected.push(lang.code);
    }
  }
  return detected;
}

/**
 * Detect which configured languages are present in the provided text (returns full LanguageOption objects).
 */
export function detectLanguageOptionsInText(text: string): LanguageOption[] {
  if (!text || typeof text !== "string") return [];
  const detected: LanguageOption[] = [];
  for (const lang of LANGUAGE_OPTIONS) {
    if (lang.regex.test(text)) {
      detected.push(lang);
    }
  }
  return detected;
}

/**
 * Checks if a member's nickname/names should be excluded based on selected languages and optional keywords.
 */
export function isNicknameExcluded(
  firstName: string = "",
  lastName: string = "",
  excludedLanguages: string[] = [],
  excludedKeywords: string[] | string = []
): { excluded: boolean; matchedLanguages: string[]; matchedKeyword?: string; reason?: string } {
  const fullName = `${firstName || ""} ${lastName || ""}`.trim();
  if (!fullName) {
    return { excluded: false, matchedLanguages: [] };
  }

  // 1. Language scripts check
  const matchedLanguages: string[] = [];
  if (Array.isArray(excludedLanguages) && excludedLanguages.length > 0) {
    for (const code of excludedLanguages) {
      const opt = LANGUAGE_OPTIONS.find(l => l.code === code);
      if (opt && opt.regex.test(fullName)) {
        matchedLanguages.push(opt.name);
      }
    }
  }

  if (matchedLanguages.length > 0) {
    return {
      excluded: true,
      matchedLanguages,
      reason: `昵称包含指定语言 [${matchedLanguages.join(", ")}]`
    };
  }

  // 2. Custom keywords check
  let keywords: string[] = [];
  if (typeof excludedKeywords === "string") {
    keywords = excludedKeywords
      .split(/[,，\n|]+/)
      .map(k => k.trim())
      .filter(Boolean);
  } else if (Array.isArray(excludedKeywords)) {
    keywords = excludedKeywords.map(k => String(k).trim()).filter(Boolean);
  }

  if (keywords.length > 0) {
    const fullNameLower = fullName.toLowerCase();
    for (const kw of keywords) {
      if (kw && fullNameLower.includes(kw.toLowerCase())) {
        return {
          excluded: true,
          matchedLanguages: [],
          matchedKeyword: kw,
          reason: `昵称包含排除关键词 "${kw}"`
        };
      }
    }
  }

  return { excluded: false, matchedLanguages: [] };
}
